import React, { DOMAttributes, ReactNode, useRef } from 'react';
import { FocusableElement, Node } from '@react-types/shared';
import { useListBoxSection } from 'react-aria/useListBox';
import { ReusableView } from 'react-stately/useVirtualizerState';
import styles from '@danske/sapphire-css/components/listbox/listbox.module.css';
import {
  useVirtualizerItem,
  VirtualizerItemOptions,
} from 'react-aria/private/virtualizer/useVirtualizerItem';
import { layoutInfoToStyle } from 'react-aria/private/virtualizer/VirtualizerItem';
import { LayoutInfo } from 'react-stately/useVirtualizerState';

import { useThemeCheck } from '../../utils/useThemeCheck';
import { Separator } from '../../Separator';

type ListBoxSectionProps<T> = Omit<VirtualizerItemOptions, 'ref'> & {
  item: Node<T>;
  childrenViews?: ReusableView<Node<T>, ReactNode>[];
  children?: ReactNode;
};

export function ListBoxSection<T extends object>({
  item,
  layoutInfo,
  virtualizer,
  childrenViews,
  children,
}: ListBoxSectionProps<T>): JSX.Element {
  useThemeCheck();

  const { groupProps, headingProps } = useListBoxSection({
    'aria-label': item['aria-label'],
    heading: item.rendered,
  });

  const headerLayoutInfo =
    childrenViews &&
    childrenViews.find((c) => c.viewType === 'header')?.layoutInfo;
  const dividerLayoutInfo =
    childrenViews &&
    childrenViews.find((c) => c.viewType === 'divider')?.layoutInfo;

  return (
    <>
      {headerLayoutInfo && (
        <div
          role="presentation"
          style={layoutInfoToStyle(headerLayoutInfo, 'ltr')}
        >
          {item.prevKey && (
            <Separator
              UNSAFE_className={styles['sapphire-listbox__separator']}
            />
          )}
          {item.rendered && (
            <SectionHeader
              item={item}
              headerLayoutInfo={headerLayoutInfo!}
              headingProps={headingProps}
              virtualizer={virtualizer}
            />
          )}
        </div>
      )}
      {layoutInfo && (
        <div
          {...groupProps}
          key={layoutInfo.key}
          style={layoutInfoToStyle(layoutInfo, 'ltr')}
          className={styles['sapphire-listbox__section']}
        >
          {children}
        </div>
      )}
      {dividerLayoutInfo && (
        <div
          role="presentation"
          style={layoutInfoToStyle(dividerLayoutInfo, 'ltr')}
        >
          <Separator UNSAFE_className={styles['sapphire-listbox__separator']} />
        </div>
      )}
    </>
  );
}

const SectionHeader = <T,>({
  item,
  headerLayoutInfo,
  headingProps,
  virtualizer,
}: {
  item: Node<T>;
  headerLayoutInfo: LayoutInfo;
  headingProps: DOMAttributes<FocusableElement>;
  virtualizer: VirtualizerItemOptions['virtualizer'];
}) => {
  const headerRef = useRef<HTMLDivElement>(null);

  useVirtualizerItem({
    layoutInfo: headerLayoutInfo!,
    virtualizer,
    ref: headerRef,
  });

  return (
    <div
      {...headingProps}
      ref={headerRef}
      className={styles['sapphire-listbox__section-header']}
    >
      {item.rendered}
    </div>
  );
};
